import {FC, useState, useEffect, useMemo, useRef, useContext} from "react";
import {makeStyles} from "tss-react/mui";
import {
	Box,
	Button,
	CircularProgress,
	Stack,
	Typography,
	Alert,
	FormControl,
	MenuItem,
	Select,
	type SelectChangeEvent,
	Dialog,
	DialogTitle,
	DialogContent,
	DialogContentText,
	DialogActions
} from "@mui/material";
import DeleteIcon from "@mui/icons-material/Delete";
import QuizOutlinedIcon from "@mui/icons-material/QuizOutlined";
import {Link} from "react-router-dom";
import {
	useGetQuiz,
	useCreateQuiz,
	useUpdateQuiz,
	useDeleteQuiz,
	useGetQuizWithQuestions,
	useGetI18nLocales,
	type QuizDetailDto,
	type CreateQuizBody,
	type QuestionSlim,
	type QuizWithQuestions,
	type UpdateQuizBody
} from "@/app2/shared/data";
import {
	App2NotificationContext,
	App2NotificationSeverity
} from "@/app2/shared/contexts/App2NotificationContext";
import {QuizAdminHeader} from "./QuizAdminHeader";
import {AdminInputText} from "@/pages/ManagerView/ContentCreation/AdminInputText";
import Editor from "@/components/admin/WYSIWYG/Editor";
import {AdminFieldContainer} from "@/pages/ManagerView/ContentCreation/AdminFieldContainer";
import {AdminInputSwitch} from "@/pages/ManagerView/ContentCreation/AdminInputSwitch";
import AdminQuizBuilder from "./AdminQuizBuilder";
import AddQuestionsModal from "./AddQuestionsModal";
import {palette} from "@/constants";

const QUIZ_TITLE_MAX_LENGTH = 255;

/** Sidebar: date-only (matches quiz admin metadata layout). */
function formatQuizDateOnly(iso: string | null | undefined): string {
	if (iso == null || iso === "") return "—";
	const d = new Date(iso);
	if (Number.isNaN(d.getTime())) return "—";
	return d.toLocaleDateString(undefined, {dateStyle: "medium"});
}

const useStyles = makeStyles()((theme) => ({
	root: {
		minHeight: "100vh",
		backgroundColor: theme.palette.background.default,
		paddingBottom: theme.spacing(4)
	},
	pageInner: {
		maxWidth: "1200px",
		margin: "0 auto",
		padding: theme.spacing(0, 3)
	},
	mainRow: {
		display: "flex",
		flexDirection: "row",
		alignItems: "flex-start",
		gap: theme.spacing(3),
		[theme.breakpoints.down("sm")]: {
			flexDirection: "column"
		}
	},
	formCard: {
		flex: 1,
		minWidth: 0,
		border: `1px solid ${theme.palette.divider}`,
		borderRadius: theme.shape.borderRadius,
		padding: theme.spacing(3),
		backgroundColor: theme.palette.background.paper
	},
	sidebarColumn: {
		width: 300,
		flexShrink: 0,
		alignSelf: "flex-start",
		position: "sticky",
		top: theme.spacing(2),
		display: "flex",
		flexDirection: "column",
		gap: theme.spacing(2),
		[theme.breakpoints.down("sm")]: {
			width: "100%",
			position: "relative",
			top: "auto"
		}
	},
	/** Draft-only indicator — sidebar only, above the Information tile (hidden when published). */
	phaseBanner: {
		width: "100%",
		boxSizing: "border-box",
		padding: theme.spacing(1.5, 2),
		borderRadius: "4px"
	},
	/** Draft-only status strip — dark brown field behind orange copy. Hidden when quiz is published. */
	phaseBannerDraft: {
		backgroundColor: palette.quizDraftBannerBg,
		border: `1px solid ${palette.orange}`
	},
	phaseBannerLine: {
		fontSize: "0.875rem",
		lineHeight: 1.45
	},
	phaseBannerEditingWord: {
		color: palette.quizDraftBannerEditing,
		fontWeight: 400
	},
	phaseBannerEmphasisDraft: {
		fontWeight: 700,
		color: palette.quizDraftBannerEmphasis
	},
	infoTile: {
		border: `1px solid ${theme.palette.divider}`,
		borderRadius: "8px",
		padding: theme.spacing(2.5, 2.5, 2.25),
		backgroundColor: theme.palette.background.paper
	},
	sidebarDeleteBtn: {
		width: "100%",
		textTransform: "uppercase",
		fontWeight: 700,
		fontSize: "0.75rem",
		borderRadius: "6px",
		padding: theme.spacing(1.25, 1.5),
		border: `1px solid ${palette.orange} !important`,
		backgroundColor: "transparent",
		color: `${palette.white} !important`,
		"&:hover": {
			backgroundColor: palette.orange08,
			borderColor: `${palette.orange} !important`
		},
		"&.Mui-disabled": {
			border: `1px solid ${palette.orange}66 !important`,
			color: `${palette.grey40} !important`,
			opacity: 1
		},
		"& .MuiSvgIcon-root": {
			color: `${palette.white} !important`
		},
		"&.Mui-disabled .MuiSvgIcon-root": {
			color: `${palette.grey40} !important`
		}
	},
	infoSectionTitle: {
		fontSize: "0.6875rem",
		fontWeight: 700,
		letterSpacing: "0.12em",
		textTransform: "uppercase",
		color: theme.palette.text.secondary,
		display: "block",
		marginBottom: theme.spacing(2),
		paddingBottom: theme.spacing(1.5),
		borderBottom: `1px solid ${theme.palette.divider}`
	},
	languageSelect: {
		width: "100%",
		"& .MuiOutlinedInput-root": {
			borderRadius: "4px",
			backgroundColor: theme.palette.mode === "dark" ? palette.greyDark : theme.palette.action.hover
		}
	},
	/** One metadata row: label left, value right (prod layout). */
	infoKvRow: {
		display: "flex",
		flexDirection: "row",
		alignItems: "baseline",
		justifyContent: "space-between",
		gap: theme.spacing(2)
	},
	infoKvLabel: {
		fontSize: "0.8125rem",
		fontWeight: 700,
		lineHeight: 1.4,
		color: theme.palette.mode === "dark" ? theme.palette.common.white : theme.palette.text.primary,
		textAlign: "left",
		flex: "1 1 auto",
		minWidth: 0
	},
	infoKvValue: {
		fontSize: "0.8125rem",
		fontWeight: 500,
		lineHeight: 1.4,
		color: theme.palette.mode === "dark" ? palette.grey80 : theme.palette.text.primary,
		textAlign: "right",
		flex: "0 0 auto",
		whiteSpace: "nowrap"
	},
	infoMetaBlock: {
		display: "flex",
		flexDirection: "column",
		gap: theme.spacing(1.75),
		marginBottom: theme.spacing(2.5),
		"&:last-child": {
			marginBottom: 0
		}
	},
	fieldLabel: {
		fontWeight: 600,
		marginBottom: theme.spacing(1),
		display: "block"
	},
	loadingContainer: {
		display: "flex",
		justifyContent: "center",
		alignItems: "center",
		minHeight: "400px"
	}
}));

interface FormState {
	title: string;
	subtitle: string;
	content: string;
	scoreRequired: number | null;
	randomiseOrder: boolean;
	composition: QuestionSlim[];
	locale: string;
}

const defaultForm: FormState = {
	title: "",
	subtitle: "",
	content: "",
	scoreRequired: null,
	randomiseOrder: false,
	composition: [],
	locale: "en"
};

export interface AdminQuizPageContentProps {
	quizId?: number;
	onSaveResponse: (data: QuizDetailDto) => void;
	onDeleteResponse: () => void;
	onGetResponse: (data: QuizDetailDto | null, statusCode?: number) => void;
}

export const AdminQuizPageContent: FC<AdminQuizPageContentProps> = ({
	quizId,
	onSaveResponse,
	onDeleteResponse,
	onGetResponse
}) => {
	const {classes} = useStyles();
	const isEdit = !!quizId;

	const [form, setForm] = useState<FormState>(defaultForm);
	const [compositionBaseline, setCompositionBaseline] = useState<QuestionSlim[]>([]);
	const [isCompositionHydrated, setIsCompositionHydrated] = useState(false);
	const [validationErrors, setValidationErrors] = useState<Record<string, string | null>>({});
	const [addQuestionsModalOpen, setAddQuestionsModalOpen] = useState(false);
	/** So Save vs Publish/Unpublish can show distinct loading labels on the secondary button. */
	const [secondaryActionPending, setSecondaryActionPending] = useState(false);
	const [deleteDialogOpen, setDeleteDialogOpen] = useState(false);
	const formRef = useRef(form);
	formRef.current = form;

	const {data: quiz, isLoading, error, isError} = useGetQuiz(quizId);
	const {
		data: quizWithQuestions,
		isError: isQuizBuilderError,
		error: quizBuilderError
	} = useGetQuizWithQuestions(isEdit ? quizId : undefined);
	const createMutation = useCreateQuiz();
	const updateMutation = useUpdateQuiz(quizId ?? 0);
	const deleteMutation = useDeleteQuiz();
	const {notify} = useContext(App2NotificationContext);
	const {data: localesResponse} = useGetI18nLocales();
	const localeOptions = localesResponse?.data ?? [];

	useEffect(() => {
		setIsCompositionHydrated(false);
	}, [quizId]);

	useEffect(() => {
		if (quiz) {
			setForm((prev) => ({
				...prev,
				title: quiz.title ?? "",
				subtitle: quiz.subtitle ?? "",
				content: quiz.content ?? "",
				scoreRequired: quiz.scoreRequired ?? null,
				randomiseOrder: quiz.randomiseOrder ?? false,
				locale: quiz.locale?.trim() ? quiz.locale : "en"
			}));
		}
	}, [quiz]);

	useEffect(() => {
		if (quizWithQuestions?.composition === undefined) return;
		const comp = quizWithQuestions.composition ?? [];
		// Stale quiz-builder cache can still have [] while local form has questions — do not wipe.
		if (comp.length === 0 && formRef.current.composition.length > 0) {
			return;
		}
		setForm((prev) => ({
			...prev,
			composition: comp
		}));
		setCompositionBaseline(comp.map((q) => ({...q})));
		setIsCompositionHydrated(true);
	}, [quizWithQuestions]);

	useEffect(() => {
		if (isError && error && "response" in error) {
			const statusCode = (error as { response?: { status?: number } }).response?.status;
			onGetResponse(null, statusCode);
		} else if (quiz && !isEdit) {
			onGetResponse(quiz);
		}
	}, [isError, error, quiz, isEdit, onGetResponse]);

	const validate = (): boolean => {
		const err: Record<string, string> = {};
		if (!form.title.trim()) {
			err.title = "Title is required";
		} else if (form.title.length > QUIZ_TITLE_MAX_LENGTH) {
			err.title = `Title cannot exceed ${QUIZ_TITLE_MAX_LENGTH} characters.`;
		}
		if (
			form.scoreRequired === null ||
			typeof form.scoreRequired !== "number" ||
			Number.isNaN(form.scoreRequired)
		) {
			err.scoreRequired = "Quiz pass threshold is required";
		}
		setValidationErrors(err);
		return Object.keys(err).length === 0;
	};

	const handleSave = async (): Promise<void> => {
		if (!validate()) return;
		const compositionPayload =
			form.composition.length > 0 ? {composition: form.composition as any} : {};
		try {
			if (isEdit) {
				const body: UpdateQuizBody = {
					title: form.title,
					subtitle: form.subtitle || null,
					content: form.content || null,
					scoreRequired: form.scoreRequired,
					randomiseOrder: form.randomiseOrder,
					locale: form.locale || "en",
					...compositionPayload
				};
				const data = await updateMutation.mutateAsync(body);
				setCompositionBaseline(form.composition.map((q) => ({...q})));
				notify(
					"Quiz saved successfully",
					"Success",
					App2NotificationSeverity.success,
					3000
				);
				onSaveResponse(data);
			} else {
				const body: CreateQuizBody = {
					title: form.title,
					subtitle: form.subtitle || null,
					content: form.content || null,
					scoreRequired: form.scoreRequired,
					randomiseOrder: form.randomiseOrder,
					...compositionPayload
				};
				const created = await createMutation.mutateAsync(body);
				notify(
					"Quiz created successfully",
					"Success",
					App2NotificationSeverity.success,
					3000
				);
				onSaveResponse({
					id: created.id,
					title: form.title,
					subtitle: form.subtitle || null,
					content: form.content || null,
					scoreRequired: form.scoreRequired,
					randomiseOrder: form.randomiseOrder
				});
			}
		} catch (e) {
			console.error("Save quiz failed", e);
			const msg =
				e && typeof e === "object" && "message" in e && typeof (e as Error).message === "string"
					? (e as Error).message
					: "Failed to save quiz";
			notify(msg, "Error", App2NotificationSeverity.error, 5000);
		}
	};

	const handlePublish = async (): Promise<void> => {
		if (!isEdit || !quizId || !quiz) return;
		if (form.composition.length === 0) return;
		if (!validate()) return;
		const compositionPayload =
			form.composition.length > 0 ? {composition: form.composition as any} : {};
		setSecondaryActionPending(true);
		try {
			const body: UpdateQuizBody = {
				title: form.title,
				subtitle: form.subtitle || null,
				content: form.content || null,
				scoreRequired: form.scoreRequired,
				randomiseOrder: form.randomiseOrder,
				locale: form.locale || "en",
				publishedAt: new Date().toISOString(),
				...compositionPayload
			};
			const data = await updateMutation.mutateAsync(body);
			setCompositionBaseline(form.composition.map((q) => ({...q})));
			notify("Quiz published successfully", "Success", App2NotificationSeverity.success, 3000);
			onSaveResponse(data as QuizDetailDto);
		} catch (e) {
			console.error("Publish quiz failed", e);
			const msg =
				e && typeof e === "object" && "message" in e && typeof (e as Error).message === "string"
					? (e as Error).message
					: "Failed to publish quiz";
			notify(msg, "Error", App2NotificationSeverity.error, 5000);
		} finally {
			setSecondaryActionPending(false);
		}
	};

	const handleUnpublish = async (): Promise<void> => {
		if (!isEdit || !quizId || !quiz) return;
		if (!validate()) return;
		const compositionPayload =
			form.composition.length > 0 ? {composition: form.composition as any} : {};
		setSecondaryActionPending(true);
		try {
			const body: UpdateQuizBody = {
				title: form.title,
				subtitle: form.subtitle || null,
				content: form.content || null,
				scoreRequired: form.scoreRequired,
				randomiseOrder: form.randomiseOrder,
				locale: form.locale || "en",
				publishedAt: null,
				...compositionPayload
			};
			const data = await updateMutation.mutateAsync(body);
			setCompositionBaseline(form.composition.map((q) => ({...q})));
			notify("Quiz unpublished successfully", "Success", App2NotificationSeverity.success, 3000);
			onSaveResponse(data as QuizDetailDto);
		} catch (e) {
			console.error("Unpublish quiz failed", e);
			const msg =
				e && typeof e === "object" && "message" in e && typeof (e as Error).message === "string"
					? (e as Error).message
					: "Failed to unpublish quiz";
			notify(msg, "Error", App2NotificationSeverity.error, 5000);
		} finally {
			setSecondaryActionPending(false);
		}
	};

	const handleDeleteConfirm = async (): Promise<void> => {
		if (!quizId) return;
		try {
			await deleteMutation.mutateAsync({quizId});
			setDeleteDialogOpen(false);
			notify(
				"Quiz deleted successfully",
				"Success",
				App2NotificationSeverity.success,
				3000
			);
			onDeleteResponse();
		} catch (e) {
			console.error("Delete quiz failed", e);
			const msg =
				e && typeof e === "object" && "message" in e && typeof (e as Error).message === "string"
					? (e as Error).message
					: "Failed to delete quiz";
			notify(msg, "Error", App2NotificationSeverity.error, 5000);
		}
	};

	const handleCompositionChange = (composition: QuestionSlim[]): void => {
		setIsCompositionHydrated(true);
		setForm((prev) => ({...prev, composition}));
	};

	const handleAddQuestions = (newQuestions?: QuestionSlim[]): void => {
		if (newQuestions && newQuestions.length > 0) {
			setIsCompositionHydrated(true);
			setForm((prev) => {
				const next = [...prev.composition, ...newQuestions];
				setCompositionBaseline(next.map((q) => ({...q})));
				return {...prev, composition: next};
			});
		}
		setAddQuestionsModalOpen(false);
	};

	const isSaving = createMutation.isPending || updateMutation.isPending;
	const isDraftEdit = isEdit && quiz && !quiz.publishedAt;
	const isPublishedEdit = isEdit && quiz && !!quiz.publishedAt;
	const publishDisabled = isSaving || form.composition.length === 0;
	const saveBtnSx = {
		textTransform: "uppercase" as const,
		fontWeight: 600,
		fontSize: "0.875rem",
		borderRadius: "6px",
		color: palette.grey70,
		borderColor: palette.orange,
		"&:hover": {
			borderColor: palette.orange,
			color: palette.white,
			backgroundColor: palette.orange08
		}
	};
	const publishBtnSx = {
		textTransform: "uppercase" as const,
		fontWeight: 700,
		fontSize: "0.875rem",
		borderRadius: "6px",
		color: `${palette.white} !important`,
		borderColor: `${palette.orange} !important`,
		"&:hover": {
			borderColor: `${palette.orange} !important`,
			backgroundColor: palette.orange08
		},
		"&.Mui-disabled": {
			color: `${palette.grey60} !important`,
			borderColor: `${palette.grey40} !important`
		}
	};

	/** Builder must use live form composition; server query alone is stale after edits. Create uses a stub (no quiz id yet). */
	const quizForBuilder: QuizWithQuestions | null = useMemo(() => {
		if (!isEdit) {
			return {
				id: 0,
				title: "",
				questions: {},
				composition: form.composition
			} as QuizWithQuestions;
		}
		if (!quizWithQuestions) return null;
		const hydratedComposition = form.composition;
		const fallbackComposition = quizWithQuestions.composition ?? [];
		return {
			...quizWithQuestions,
			composition: isCompositionHydrated ? hydratedComposition : fallbackComposition
		};
	}, [isEdit, form.composition, quizWithQuestions, isCompositionHydrated]);

	if (isEdit && isLoading) {
		return (
			<Box className={classes.root}>
				<Box className={classes.loadingContainer}>
					<CircularProgress />
				</Box>
			</Box>
		);
	}

	if (isEdit && error) {
		return (
			<Box className={classes.root}>
				<Box className={classes.pageInner}>
					<Alert severity="error">Failed to load quiz: {(error as Error).message}</Alert>
				</Box>
			</Box>
		);
	}

	return (
		<Box className={classes.root}>
			<Box className={classes.pageInner}>
				<QuizAdminHeader
					rootSlug="/admin/quizzes"
					rootBreadcrumb={
						<>
							<QuizOutlinedIcon />
							<Link to="/admin/quizzes">
								<Typography variant="body1">Quizzes</Typography>
							</Link>
						</>
					}
					editMode={isEdit ? "edit" : "create"}
					quiz={
						quiz
							? {
								id: quiz.id,
								title: quiz.title,
								publishedAt: quiz.publishedAt
							}
							: null
					}
					title={isEdit ? (quiz?.title ?? "") : "Create quiz"}
					actionButtons={[
						{
							label: isSaving ? "Saving…" : "Save",
							variant: "outlined",
							onClick: handleSave,
							disabled: isSaving,
							sx: saveBtnSx
						},
						...(isDraftEdit
							? [
								{
									label: secondaryActionPending ? "Publishing…" : "Publish",
									variant: "outlined" as const,
									onClick: handlePublish,
									disabled: publishDisabled,
									sx: publishBtnSx
								}
							]
							: isPublishedEdit
								? [
									{
										label: secondaryActionPending ? "Unpublishing…" : "Unpublish",
										variant: "outlined" as const,
										onClick: handleUnpublish,
										disabled: isSaving,
										sx: publishBtnSx
									}
								]
								: [])
					]}
				/>

				{(createMutation.isError || updateMutation.isError) && (
					<Alert severity="error" sx={{mb: 2}}>
						{(createMutation.error as Error)?.message ??
							(updateMutation.error as Error)?.message ??
							"An error occurred"}
					</Alert>
				)}

				<Box className={classes.mainRow}>
					<Box className={classes.formCard}>
						<Stack spacing={4}>
							<AdminInputText
								label="Quiz title"
								labelPlacement="above"
								required
								value={form.title}
								inputProps={{maxLength: QUIZ_TITLE_MAX_LENGTH}}
								helperText={
									validationErrors.title
										? undefined
										: `${form.title.length} / ${QUIZ_TITLE_MAX_LENGTH} characters`
								}
								FormHelperTextProps={{sx: {mt: 0.5}}}
								onChange={(e) => {
									setForm((prev) => ({...prev, title: e.target.value}));
									if (validationErrors.title) {
										setValidationErrors((prev) => ({...prev, title: null}));
									}
								}}
								error={validationErrors.title ?? undefined}
							/>
							<AdminInputText
								label="Quiz subtitle"
								labelPlacement="above"
								multiline
								rows={2}
								value={form.subtitle}
								onChange={(e) => setForm((prev) => ({...prev, subtitle: e.target.value}))}
							/>
							<Box>
								<Typography component="label" className={classes.fieldLabel}>
								Content
								</Typography>
								<AdminFieldContainer>
									<Editor
										value={form.content}
										onChange={(e) => setForm((prev) => ({...prev, content: e.target.value}))}
									/>
								</AdminFieldContainer>
							</Box>
							<AdminInputText
								label="Quiz Pass Threshold"
								labelPlacement="above"
								required
								type="number"
								value={form.scoreRequired ?? ""}
								onChange={(e) => {
									const v = e.target.value;
									setForm((prev) => ({
										...prev,
										scoreRequired:
										v === ""
											? null
											: (() => {
												const n = parseInt(v, 10);
												return Number.isNaN(n) ? null : n;
											})()
									}));
								}}
								error={validationErrors.scoreRequired ?? undefined}
							/>
							<AdminInputSwitch
								label="Randomize Question Order"
								labelPlacement="above"
								checked={form.randomiseOrder}
								onChange={(e) =>
									setForm((prev) => ({...prev, randomiseOrder: e.target.checked}))
								}
							/>
							<Box>
								<Typography
									component="label"
									className={classes.fieldLabel}
									sx={(theme) => ({
										"& .req": {color: theme.palette.error.main}
									})}
								>
								Questions<span className="req"> *</span>
								</Typography>
								{isEdit && isQuizBuilderError ? (
									<Alert severity="error">
										Failed to load questions:{" "}
										{(quizBuilderError as Error)?.message ?? "Unknown error"}
									</Alert>
								) : quizForBuilder ? (
									<AdminQuizBuilder
										quiz={quizForBuilder}
										compositionResetBaseline={compositionBaseline}
										onChange={handleCompositionChange}
										onQuestionAdd={() => setAddQuestionsModalOpen(true)}
									/>
								) : (
									<Box sx={{display: "flex", alignItems: "center", gap: 1}}>
										<CircularProgress size={20} />
										<Typography variant="body2" color="text.secondary">
											Loading questions…
										</Typography>
									</Box>
								)}
							</Box>
						</Stack>
					</Box>

					<Box className={classes.sidebarColumn} component="aside" aria-label="Quiz metadata">
						{isEdit && quiz && !quiz.publishedAt ? (
							<Box
								className={`${classes.phaseBanner} ${classes.phaseBannerDraft}`}
								role="status"
							>
								<Typography component="div" className={classes.phaseBannerLine}>
									<Box component="span" className={classes.phaseBannerEditingWord}>
										Editing
									</Box>{" "}
									<Box component="span" className={classes.phaseBannerEmphasisDraft}>
										Draft version
									</Box>
								</Typography>
							</Box>
						) : null}
						<Box className={classes.infoTile}>
							<Typography component="h2" className={classes.infoSectionTitle}>
								Information
							</Typography>
							<Box className={classes.infoMetaBlock}>
								<Box className={classes.infoKvRow}>
									<Typography component="span" className={classes.infoKvLabel}>
										Last update
									</Typography>
									<Typography component="span" className={classes.infoKvValue}>
										{isEdit ? formatQuizDateOnly(quiz?.updatedAt) : "—"}
									</Typography>
								</Box>
								<Box className={classes.infoKvRow}>
									<Typography component="span" className={classes.infoKvLabel}>
										By
									</Typography>
									<Typography component="span" className={classes.infoKvValue}>
										{isEdit && quiz?.updatedBy?.trim() ? quiz.updatedBy : "—"}
									</Typography>
								</Box>
							</Box>
							<Box className={classes.infoMetaBlock}>
								<Box className={classes.infoKvRow}>
									<Typography component="span" className={classes.infoKvLabel}>
										Created
									</Typography>
									<Typography component="span" className={classes.infoKvValue}>
										{isEdit ? formatQuizDateOnly(quiz?.createdAt) : "—"}
									</Typography>
								</Box>
								<Box className={classes.infoKvRow}>
									<Typography component="span" className={classes.infoKvLabel}>
										By
									</Typography>
									<Typography component="span" className={classes.infoKvValue}>
										{isEdit && quiz?.createdBy?.trim() ? quiz.createdBy : "—"}
									</Typography>
								</Box>
							</Box>
							{isEdit ? (
								<>
									<Typography component="h2" className={classes.infoSectionTitle}>
										Language
									</Typography>
									<FormControl className={classes.languageSelect} size="small">
										<Select
											displayEmpty
											value={form.locale}
											onChange={(e: SelectChangeEvent<string>) =>
												setForm((prev) => ({...prev, locale: e.target.value}))
											}
											disabled={isSaving || localeOptions.length === 0}
											inputProps={{"aria-label": "Quiz language"}}
										>
											{form.locale &&
											!localeOptions.some((l) => l.code === form.locale) ? (
													<MenuItem value={form.locale}>{form.locale}</MenuItem>
												) : null}
											{localeOptions.map((loc) => (
												<MenuItem key={loc.code} value={loc.code}>
													{loc.name}
												</MenuItem>
											))}
										</Select>
									</FormControl>
								</>
							) : null}
						</Box>
						{isEdit && (
							<Button
								className={classes.sidebarDeleteBtn}
								variant="outlined"
								fullWidth
								startIcon={<DeleteIcon />}
								disabled={deleteMutation.isPending}
								onClick={() => setDeleteDialogOpen(true)}
							>
								{deleteMutation.isPending ? "Deleting…" : "Delete this quiz"}
							</Button>
						)}
					</Box>
				</Box>
			</Box>

			{addQuestionsModalOpen && (
				<AddQuestionsModal
					open={addQuestionsModalOpen}
					currentQuestions={form.composition}
					onClose={handleAddQuestions}
				/>
			)}

			<Dialog open={deleteDialogOpen} onClose={() => !deleteMutation.isPending && setDeleteDialogOpen(false)}>
				<DialogTitle>Delete quiz</DialogTitle>
				<DialogContent>
					<DialogContentText sx={{color: "common.white"}}>
						Are you sure you want to delete this quiz?
					</DialogContentText>
				</DialogContent>
				<DialogActions>
					<Button onClick={() => setDeleteDialogOpen(false)} disabled={deleteMutation.isPending}>
						Cancel
					</Button>
					<Button
						onClick={handleDeleteConfirm}
						color="error"
						variant="contained"
						disabled={deleteMutation.isPending}
					>
						{deleteMutation.isPending ? "Deleting…" : "Delete"}
					</Button>
				</DialogActions>
			</Dialog>
		</Box>
	);
};
